/*
 * FICHIER: App.js
 * BUT: Composant principal de l'application OffiSport
 * 
 * Ce fichier définit la structure globale de l'application :
 * - Configuration du routage (navigation entre les pages)
 * - Gestion des contextes (données partagées)
 * - Intégration des outils de tracking (Matomo)
 * - Application des styles globaux
 */

// Déclenchement CI/CD : modification temporaire

// Import de React - Bibliothèque pour créer des composants
import React, { useState, useEffect, lazy, Suspense } from 'react';
// Filtrage global des logs: ne laisser passer que [API][CDM]/[TIS]/[OSARC]
import './utils/setupLogging';

// Import des outils de navigation React Router
// BrowserRouter: Permet la navigation dans l'app sans recharger la page
// Routes: Conteneur pour définir toutes les routes
// Route: Définit une route spécifique (URL -> Composant)
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// HelmetProvider: Permet de modifier les meta-données de la page (titre, description, etc.)
import { HelmetProvider } from 'react-helmet-async';

// NavigationProvider: Contexte personnalisé pour gérer l'état de navigation
import { NavigationProvider } from './contexts/NavigationContext';

// Styles CSS globaux appliqués à toute l'application
import GlobalStyles from './components/common/GlobalStyles';

// Hooks personnalisés pour le tracking avec Matomo (outil d'analyse web)
import { useMatomo, useErrorTracking } from './hooks/useMatomo';

// Layout principal qui contient la structure commune (header, footer, etc.)
import MainLayout from './layouts/MainLayout';

import PageLoadFallback from './components/common/PageLoadFallback';

const Accueil = lazy(() => import('./pages/Accueil'));
const Actualites = lazy(() => import('./pages/Actualites'));
const ActivitesPage = lazy(() => import('./pages/ActivitesPage'));
const CategoryDetail = lazy(() => import('./pages/CategoryDetail'));
const Evenements = lazy(() => import('./pages/Evenements'));
const Clubs = lazy(() => import('./pages/Clubs'));
const Bibliotheque = lazy(() => import('./pages/Bibliotheque'));
const Partenaires = lazy(() => import('./pages/Partenaires'));

// Import de l'API Strapi pour vérifier le backend (maintenance)
import { checkAppAccess } from './api/strapi';
import MaintenancePage from './components/layout/MaintenancePage';

// Garde backend : affiche la page maintenance si le backend est injoignable
// OU si le champ ModeMaintenance est activé et publié dans Strapi (single type Accueil).
function BackendGuard({ children }) {
  const [status, setStatus] = useState({ backendOk: null, maintenance: false });
  const [checking, setChecking] = useState(true);

  const runCheck = async () => {
    setChecking(true);
    const result = await checkAppAccess();
    setStatus(result);
    setChecking(false);
  };

  useEffect(() => {
    runCheck();
  }, []);

  if (checking) {
    return null;
  }
  if (!status.backendOk || status.maintenance) {
    return <MaintenancePage onRetry={runCheck} />;
  }
  return children;
}

// Composant interne pour utiliser les hooks avec BrowserRouter
// Les hooks ne peuvent être utilisés qu'à l'intérieur d'un BrowserRouter
function AppContent() {
  // Activation du tracking automatique des pages visitées
  useMatomo();
  
  // Activation du tracking automatique des erreurs JavaScript
  useErrorTracking();

  return (
    <BackendGuard>
      <Suspense fallback={<PageLoadFallback />}>
        <Routes>
          <Route path="/" element={<MainLayout />}>
            <Route index element={<Accueil />} />
            <Route path="accueil" element={<Accueil />} />
            <Route path="actualites" element={<Actualites />} />
            <Route path="activites" element={<ActivitesPage />} />
            <Route path="evenements" element={<Evenements />} />
            <Route path="clubs" element={<Clubs />} />
            <Route path="bibliotheque" element={<Bibliotheque />} />
            <Route path="partenaires" element={<Partenaires />} />
            <Route path="actualites/:id" element={<Actualites />} />
            <Route path="activites/:id" element={<ActivitesPage />} />
            <Route path="activites/:categoryId" element={<CategoryDetail />} />
            <Route path="evenements/:id" element={<Evenements />} />
            <Route path="clubs/:id" element={<Clubs />} />
            <Route path="bibliotheque/:id" element={<Bibliotheque />} />
            <Route path="partenaires/:id" element={<Partenaires />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BackendGuard>
  );
}

// Composant principal App - Point d'entrée de l'application
function App() {
  return (
    // HelmetProvider: Permet de gérer les meta-données de chaque page
    <HelmetProvider>
      {/* NavigationProvider: Fournit un contexte global pour la navigation */}
      <NavigationProvider>
        
        {/* Application des styles CSS globaux */}
        <GlobalStyles />
        
        {/* BrowserRouter: Active le système de navigation par URL */}
        <BrowserRouter>
          {/* AppContent contient toutes les routes et le tracking */}
          <AppContent />
        </BrowserRouter>
        
      </NavigationProvider>
    </HelmetProvider>
  );
}

// Export du composant pour qu'il puisse être importé dans index.js
export default App;
